import React, { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ModalActionTypes, Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import { compose } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import { CTAContainer } from 'components/CTAContainer'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import {
  AcceptanceDocument,
  ActionType,
  BankCardOrder,
  BankCardTier,
  BankType,
  KycStatus,
  OrderBankCardInput,
  PageName,
  RequestVersions,
  Scalars,
} from 'generated/types'
import { getEmploymentStatus } from 'utils/employmentStatus'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { PromptProps, withPrompt } from 'utils/prompts'

import { CheckingAccountApprovedScreen } from './card-order-status/CheckingAccountApprovedScreen'
import { CardBackRotate } from './CardBackRotate'
import { useCardOrderReviewTracking } from './events'
import AgreeToAcceptanceDocumentMutation from './gql/agree-to-acceptance-document-mutation.gql'
import OrderBankCardMutation from './gql/order-bank-card-mutation.gql'
import { HardFailScreen } from './HardFailScreen'
import {
  AgreementCard,
  BodyText,
  ConfirmScreenRoundCheckboxInput,
  Content,
  NextButton,
  StyledAnchor,
  TitleText,
} from './styled-components'
import { useCardOrderReviewData } from './useCardOrderReviewData'
import { useCardStatusPolling } from './useCardStatusPolling'
import { VerifyingLoaderScreen } from './VerifyingLoaderScreen'

type CardOrderReviewProps = WithFlowRouterProps & PromptProps

const enhance = compose<CardOrderReviewProps, {}>(withFlowRouter, withPrompt)

const DocLink = ({ children, href }: { children: string; href: string }) => (
  <StyledAnchor href={href} key={href} rel="noopener noreferrer" target="_blank">
    {children}
  </StyledAnchor>
)

const eSignLabel = (eSignConsentUrl: string) => (
  <span>
    I have read and agree to the&nbsp;
    <DocLink href={eSignConsentUrl}>E-Sign Consent</DocLink>
  </span>
)

const bankDocumentsLabel = (acceptanceDocuments: AcceptanceDocument[]) => (
  <span>
    I have read and agree to the&nbsp;
    {acceptanceDocuments
      .filter(doc => doc.type !== 'spend_esign_consent_nbkc')
      .map((bankDocument, index) => (
        <span key={`${bankDocument.url}${index}`}>
          {index === acceptanceDocuments.length - 2 && 'and '}
          <DocLink href={bankDocument.url}>{bankDocument.name}</DocLink>
          {index < acceptanceDocuments.length - 2 ? ', ' : '.'}
        </span>
      ))}
  </span>
)

const CardOrderReview: React.FC<CardOrderReviewProps> = ({ route, promptUser }) => {
  const { getRemoteString } = useRemoteStrings()
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const track = useCardOrderReviewTracking()
  const location = useLocation<{ hasOrderedCard?: boolean }>()

  // State
  const [eSignature, setESignature] = useState(false)
  const [bankDocuments, setBankDocuments] = useState(false)
  const [documentsAccepted, setDocumentsAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Networking
  const employmentStatus = getEmploymentStatus() // Needs to be passed to order bank card mutation
  const {
    loading: loadingReviewData,
    signatureSvg,
    acceptanceDocuments,
    eSignUrl,
  } = useCardOrderReviewData()

  const nextStep = () => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.CardOrderReview, ActionType.Continue)
      : route({ from: FlowRoute.CardOrderReview, action: 'continue' })
  }

  const { kycStatus, startPolling } = useCardStatusPolling(cardStatusOnResolve => {
    switch (cardStatusOnResolve) {
      case 'success':
        // Show the success checkmark for a couple of seconds before routing
        setTimeout(nextStep, 2000)
        break
      case 'actionRequired':
        // Soft fail user - don't show the success checkmark and proceed them through
        nextStep()
        break
      case 'timeout':
        showTimeoutPrompt()
        break
    }
  })

  const [agreeToAcceptanceDocument] = useMutation<void, { id: Scalars['ID'] }>(
    AgreeToAcceptanceDocumentMutation,
  )

  const [orderBankCard] = useMutation<BankCardOrder, OrderBankCardInput>(OrderBankCardMutation, {
    variables: {
      isDocumentsAccepted: documentsAccepted,
      bankType: BankType.Nbkc,
      employmentStatus,
      requestVersion: RequestVersions.September_2022,
      bankCardTier: BankCardTier.MightyOak,
    },
    onCompleted: () => {
      acceptanceDocuments.forEach(({ id }) =>
        agreeToAcceptanceDocument({
          variables: { id },
        }),
      )
      startPolling()
    },
    onError: () => {
      setSubmitting(false)
      setESignature(false)
      setBankDocuments(false)
    },
  })

  // Computed Properties
  const shouldShowBack = !kycStatus

  // Actions
  const showTimeoutPrompt = () => {
    track.timeoutContainerViewed()
    promptUser({
      title: getRemoteString({
        key: 'error.checking.card_order.loading.verification_status_polling.title',
      }),
      message: getRemoteString({
        key: 'error.checking.card_order.loading.verification_status_polling.body',
      }),
      actions: [
        {
          type: ModalActionTypes.primary,
          label: 'Okay',
        },
      ],
    }).then(() => {
      track.timeoutButtonTapped()
      nextStep()
    })
  }

  const handleSubmitButton = () => {
    track.confirmTapped()
    setSubmitting(true)
    orderBankCard()
  }

  useEffect(() => {
    // Employment status can only be saved to localStorage atm
    // If it's not set for some reason, we just take the user back to the occupation
    // page so they can provide one.
    if (!location.state?.hasOrderedCard && (!employmentStatus || !employmentStatus.length)) {
      window.location.replace(FlowRoute.Occupation)
    }
  }, [employmentStatus])

  useEffect(() => {
    setDocumentsAccepted(eSignature && bankDocuments)
  }, [bankDocuments, eSignature])

  if (loadingReviewData) {
    return <CenteredSpinner />
  }

  let content = (
    <>
      <Content>
        <TitleText>{getRemoteString({ key: 'registration.card_order.review.header' })}</TitleText>
        <BodyText>{getRemoteString({ key: 'registration.card_order.review.body' })}</BodyText>

        <CardBackRotate autoRotate={true} svgFile={signatureSvg} />

        <AgreementCard>
          {eSignUrl && (
            <ConfirmScreenRoundCheckboxInput
              id="eSignature"
              label={eSignLabel(eSignUrl)}
              name="eSignature"
              onChange={() => setESignature(prev => !prev)}
              checked={eSignature}
            />
          )}
          <ConfirmScreenRoundCheckboxInput
            id="bankDocuments"
            label={bankDocumentsLabel(acceptanceDocuments)}
            name="bankDocuments"
            onChange={() => setBankDocuments(prev => !prev)}
            checked={bankDocuments}
          />
        </AgreementCard>
      </Content>

      <Separator />
      <CTAContainer>
        <NextButton disabled={!documentsAccepted} onPress={handleSubmitButton}>
          {submitting ? (
            <div>
              <Spinner height={20} width={20} white />
            </div>
          ) : (
            getRemoteString({ key: 'registration.card_order.review.cta' })
          )}
        </NextButton>
      </CTAContainer>
    </>
  )

  if (kycStatus === KycStatus.Accepted) {
    content = <CheckingAccountApprovedScreen />
  } else if (kycStatus === KycStatus.Failure) {
    content = <HardFailScreen />
  } else if (submitting || kycStatus === KycStatus.Pending) {
    content = <VerifyingLoaderScreen />
  }

  return (
    <>
      <Nav back={shouldShowBack} />
      <Layout>{content}</Layout>
    </>
  )
}

export default enhance(CardOrderReview)
